import React, { useEffect, useState } from "react";
import { fetchCollectionByGid } from "@/lib/shopifyStorefront";
import { addVariantToCart } from "@/lib/shopifyCart";

/**
 * FeaturedProductGrid
 * ───────────────────
 * CollectorUp-native visual renderer for the Featured Collectibles
 * section. Shopify still owns everything commercial (product data via
 * Storefront API + Add to Cart via shared shopifyCart drawer).
 *
 * Layout notes:
 *   • Image + title + "View item" all navigate to `product_url` in the
 *     SAME TAB (no target="_blank") so mobile back returns to /shop.
 *   • Card padding/button height tightened for a slimmer /shop.
 */
export default function FeaturedProductGrid() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchCollectionByGid("gid://shopify/Collection/318628200514", 24);
      if (!cancelled) { setProducts(r.products); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
           data-testid="featured-grid-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-neutral-100 aspect-[3/4] animate-pulse" />
        ))}
      </div>
    );
  }

  if (!products.length) {
    return (
      <div data-testid="featured-grid-empty"
           className="rounded-2xl border-2 border-dashed border-neutral-300 p-8 text-center text-sm text-[var(--smoke)]">
        No featured products available.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
         data-testid="featured-grid">
      {products.map((p) => (
        <FeaturedCard key={p.shopify_product_id} product={p} />
      ))}
    </div>
  );
}

function FeaturedCard({ product }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");
  const {
    product_title, image_url, price, availability,
    shopify_variant_id, product_url, shopify_handle,
  } = product;

  const soldOut = availability === "SOLD_OUT" || !shopify_variant_id;

  const onAdd = async () => {
    if (soldOut || busy) return;
    setBusy(true); setErr("");
    const r = await addVariantToCart(shopify_variant_id, 1);
    setBusy(false);
    if (!r.ok) setErr("Could not open cart — please refresh.");
  };

  const linkProps = product_url ? { href: product_url } : {};

  const Image = (
    <div className="aspect-square bg-neutral-50 overflow-hidden">
      {image_url ? (
        <img src={image_url} alt={product_title || ""} loading="lazy"
             className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>
      )}
    </div>
  );

  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)]/15 bg-white overflow-hidden flex flex-col shadow-[4px_4px_0_var(--ink)]"
      data-testid={`featured-card-${shopify_handle || "x"}`}
    >
      {product_url ? (
        <a {...linkProps}
           data-testid={`featured-card-image-${shopify_handle || "x"}`}
           aria-label={product_title || "Featured item"}
           className="block">
          {Image}
        </a>
      ) : Image}

      <div className="p-2.5 md:p-3 flex flex-col gap-1 flex-1">
        {product_url ? (
          <a {...linkProps}
             className="font-display text-xs md:text-sm leading-tight line-clamp-2 min-h-[2.2em] hover:underline"
             data-testid={`featured-card-title-${shopify_handle || "x"}`}>
            {product_title}
          </a>
        ) : (
          <div className="font-display text-xs md:text-sm leading-tight line-clamp-2 min-h-[2.2em]">
            {product_title}
          </div>
        )}
        <div className="text-base md:text-lg font-display leading-none">
          {price || <span className="text-[var(--smoke)] text-xs">—</span>}
        </div>
        <div className="mt-auto flex flex-col gap-0.5 pt-1">
          <button
            type="button"
            onClick={onAdd}
            disabled={soldOut || busy}
            className={`btn-yellow btn-yellow--slim w-full justify-center ${
              soldOut || busy ? "opacity-60 cursor-not-allowed" : ""
            }`}
            data-testid={`featured-card-cta-${shopify_handle || "x"}`}
            data-variant-id={shopify_variant_id || ""}
          >
            {soldOut ? "SOLD OUT" : busy ? "ADDING…" : "ADD TO CART"}
          </button>
          {product_url ? (
            <a {...linkProps}
               className="text-[10px] md:text-[11px] text-[var(--smoke)] hover:text-[var(--ink)] text-center underline underline-offset-2"
               data-testid={`featured-card-view-${shopify_handle || "x"}`}>
              View item
            </a>
          ) : null}
          {err ? <div role="alert" className="text-[11px] text-red-700">{err}</div> : null}
        </div>
      </div>
    </div>
  );
}
